import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useCancelOrder, useDeclinePriceNegotiation } from '~/api';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { CircleCheckBig, RefreshCw, Truck, XCircle } from 'lucide-react';
import { CancelOrderDialog } from '~/pages/order-view/sections/CancelOrderDialog';
import { DeclinePriceNegotiationDialog } from '~/pages/order-view/sections/DeclinePriceNegotiationDialog';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui';
import type { OrderStatus } from '~/graphql/graphql.ts';
import { useShipOrderModal } from '~/pages/order-view/sections/useShipOrderModal';
import { useConfirmOrderModal } from '~/pages/order-view/sections/useConfirmOrderModal';
import { extractGraphQLErrorMessages } from '~/utils';

export function SellerOrderCallToAction({
  orderId,
  orderStatus,
  awaitingPurchaseOrderNumber,
  canReturnToDraft,
  hasSuggestedPrice,
  hideShipButton,
}: {
  orderId: string;
  orderStatus: OrderStatus;
  awaitingPurchaseOrderNumber: boolean;
  canReturnToDraft: boolean;
  hasSuggestedPrice?: boolean;
  /** True when a trader's Sourcing panel already renders the ship action for a CONFIRMED order. */
  hideShipButton?: boolean;
}) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { mutate: cancelOrder, isPending: isCancellingOrder } =
    useCancelOrder();
  const {
    mutate: declinePriceNegotiation,
    isPending: isDecliningPriceNegotiation,
  } = useDeclinePriceNegotiation(orderId);
  const { shipModal, openShipModal, isShippingOrder } =
    useShipOrderModal(orderId);
  const { confirmModal, openConfirmModal, isConfirmingOrder } =
    useConfirmOrderModal(orderId);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  if (
    !['PENDING_SELLER_CONFIRMATION', 'CONFIRMED', 'BLOCKED'].includes(
      orderStatus
    )
  ) {
    return null;
  }
  if (orderStatus === 'CONFIRMED' && hideShipButton) {
    // A trader's Sourcing panel renders the fulfilment action instead.
    return null;
  }

  const handleCancelOrder = (note: string) => {
    cancelOrder(
      { id: orderId, note },
      {
        onSuccess: () => {
          setShowCancelModal(false);
          toast.success(t('order.cancel_order_success'));
          navigate('/seller-orders');
        },
        onError: (error) => {
          toast.error(
            t('order.cancel_order_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          );
        },
      }
    );
  };

  const handleDeclinePriceNegotiation = (reason: string) => {
    declinePriceNegotiation(
      { orderId, reason },
      {
        onSuccess: () => {
          setShowDeclineModal(false);
        },
      }
    );
  };

  return (
    <>
      {shipModal}
      {confirmModal}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)}>
        <CancelOrderDialog
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelOrder}
          isLoading={isCancellingOrder}
        />
      </Modal>
      <Modal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
      >
        <DeclinePriceNegotiationDialog
          onClose={() => setShowDeclineModal(false)}
          onConfirm={handleDeclinePriceNegotiation}
          isLoading={isDecliningPriceNegotiation}
        />
      </Modal>
      <div className="flex flex-col gap-3">
        {orderStatus === 'PENDING_SELLER_CONFIRMATION' && (
          <>
            <Button
              onClick={openConfirmModal}
              disabled={isConfirmingOrder || awaitingPurchaseOrderNumber}
              title={
                awaitingPurchaseOrderNumber
                  ? t('order.confirm_order_awaiting_po_number')
                  : undefined
              }
            >
              <CircleCheckBig size={18} />
              {t('order.confirm_order')}
            </Button>
            {awaitingPurchaseOrderNumber && (
              <p className="text-xs text-neut-500">
                {t('order.confirm_order_awaiting_po_number')}
              </p>
            )}
            {hasSuggestedPrice && !canReturnToDraft && (
              <p className="text-xs text-neut-500">
                {t('order.decline_price_negotiation_in_msr')}
              </p>
            )}
            {hasSuggestedPrice && canReturnToDraft ? (
              <button
                onClick={() => setShowDeclineModal(true)}
                disabled={isDecliningPriceNegotiation}
                className="w-full py-3 bg-white border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} />
                {t('order.decline_price_negotiation')}
              </button>
            ) : (
              <Button
                variant="destructive-outline"
                onClick={() => setShowCancelModal(true)}
                disabled={isCancellingOrder}
              >
                <XCircle size={18} />
                {t('order.cancel_order')}
              </Button>
            )}
          </>
        )}
        {orderStatus === 'CONFIRMED' && !hideShipButton && (
          <Button onClick={openShipModal} disabled={isShippingOrder}>
            <Truck size={18} />
            {t('order.ship_order')}
          </Button>
        )}
        {orderStatus === 'BLOCKED' && (
          <Button
            variant="destructive-outline"
            onClick={() => setShowCancelModal(true)}
            disabled={isCancellingOrder}
          >
            <XCircle size={18} />
            {t('order.cancel_order')}
          </Button>
        )}
      </div>
    </>
  );
}
